/*
 * MMA Cart Drawer controller
 *
 * Ships DORMANT. Loading this file changes nothing on the page.
 * The Visually.io variant calls window.MMACartDrawer.enable() to activate it.
 *
 * Config is injected by sections/cart-drawer.liquid as window.MMACartDrawerConfig.
 */
(function (root) {
  'use strict';

  /* ---------------------------------------------------------------- pure logic
   * Everything in this block is side-effect free and unit tested.
   */

  function moneyFormat(cents, format) {
    var fmt = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    if (!match) return fmt;

    function group(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';
      var n = (number / 100.0).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents_ = parts[1] ? decimal + parts[1] : '';
      return dollars + cents_;
    }

    var value;
    switch (match[1]) {
      case 'amount':
        value = group(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = group(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = group(cents, 0, ' ', ',');
        break;
      default:
        value = group(cents, 2, ',', '.');
    }
    return fmt.replace(placeholder, value);
  }

  /* The presentment -> shop-base conversion rate. Shopify Markets exposes this
   * as Shopify.currency.rate. Absent (single currency store) it is 1.
   * This replaces the hardcoded `const exchangeRate = 110` in the legacy snippet.
   */
  function baseRate(shopify) {
    var s = shopify || root.Shopify;
    var r = s && s.currency && parseFloat(s.currency.rate);
    return r && !isNaN(r) && r > 0 ? r : 1;
  }

  function isInsuranceLine(item, insuranceProductId) {
    if (!insuranceProductId) return false;
    return String(item.product_id) === String(insuranceProductId);
  }

  /* Cart value used for TIER SELECTION.
   * Deliberately excludes insurance line items and converts to shop base
   * currency. Excluding insurance is what prevents the feedback loop where
   * adding a $9.95 policy pushes the cart across a tier boundary, which
   * re-tiers, which changes the total again.
   */
  function goodsBaseCents(cart, insuranceProductId, rate) {
    var cents = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isInsuranceLine(items[i], insuranceProductId)) continue;
      cents += items[i].final_line_price;
    }
    return Math.round(cents / (rate || 1));
  }

  /* Tiers are [{ max: <dollars, exclusive>, variantId: '...' }, ...] ascending.
   * The final tier acts as the cap for anything above its max, which is the
   * agreed behaviour for carts over $500.
   */
  function tierFor(baseCents, tiers) {
    if (!tiers || !tiers.length) return null;
    var dollars = baseCents / 100;
    for (var i = 0; i < tiers.length; i++) {
      if (dollars < Number(tiers[i].max)) return tiers[i];
    }
    return tiers[tiers.length - 1];
  }

  function insuranceLines(cart, insuranceProductId) {
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isInsuranceLine(items[i], insuranceProductId)) out.push(items[i]);
    }
    return out;
  }

  /* Returns a /cart/update.js `updates` payload that reconciles the cart to the
   * desired insurance state, or null when the cart is already correct.
   * Keyed by variant id; 0 removes.
   */
  function insuranceUpdates(cart, cfg, wanted) {
    var lines = insuranceLines(cart, cfg.insuranceProductId);
    var rate = baseRate();
    var desired = wanted ? tierFor(goodsBaseCents(cart, cfg.insuranceProductId, rate), cfg.tiers) : null;
    var updates = {};
    var changed = false;

    for (var i = 0; i < lines.length; i++) {
      var vid = String(lines[i].variant_id);
      if (desired && vid === String(desired.variantId) && lines[i].quantity === 1) continue;
      updates[vid] = 0;
      changed = true;
    }

    if (desired) {
      var already = false;
      for (var j = 0; j < lines.length; j++) {
        if (String(lines[j].variant_id) === String(desired.variantId) && lines[j].quantity === 1) already = true;
      }
      if (!already) {
        updates[String(desired.variantId)] = 1;
        changed = true;
      }
    }

    return changed ? updates : null;
  }

  function isInsuranceChecked(cart, insuranceProductId) {
    return insuranceLines(cart, insuranceProductId).length > 0;
  }

  /* Progress bar uses the FULL cart total including insurance, matching the
   * existing cart page behaviour. Threshold is stored in shop base cents.
   */
  function shippingProgress(cart, thresholdBaseCents, rate) {
    var r = rate || 1;
    var thresholdPresentment = Math.round(thresholdBaseCents * r);
    var total = (cart && cart.total_price) || 0;
    var remaining = Math.max(0, thresholdPresentment - total);
    var percent = thresholdPresentment > 0 ? Math.min(100, (total / thresholdPresentment) * 100) : 100;
    return {
      remaining: remaining,
      percent: Math.round(percent * 10) / 10,
      qualified: remaining === 0
    };
  }

  /* Shopify cart permalink: /cart/<variant>:<qty>,<variant>:<qty>
   * Insurance is excluded so a shared cart re-tiers for the recipient rather
   * than inheriting the sender's policy.
   */
  function cartPermalink(cart, origin, insuranceProductId) {
    var parts = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (isInsuranceLine(items[i], insuranceProductId)) continue;
      parts.push(items[i].variant_id + ':' + items[i].quantity);
    }
    if (!parts.length) return null;
    return (origin || '') + '/cart/' + parts.join(',');
  }

  /* Upsells already in the cart should not be offered again. */
  function filterUpsells(upsells, cart) {
    var inCart = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) inCart[String(items[i].product_id)] = true;
    return (upsells || []).filter(function (u) {
      return !inCart[String(u.productId)];
    });
  }

  var logic = {
    moneyFormat: moneyFormat,
    baseRate: baseRate,
    goodsBaseCents: goodsBaseCents,
    tierFor: tierFor,
    insuranceLines: insuranceLines,
    insuranceUpdates: insuranceUpdates,
    isInsuranceChecked: isInsuranceChecked,
    shippingProgress: shippingProgress,
    cartPermalink: cartPermalink,
    filterUpsells: filterUpsells
  };

  /* Node/test consumers stop here. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = logic;
  }
  if (typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- controller */

  /* Config is read from the JSON tag the section renders. The Section Rendering
   * API re-injects markup via innerHTML, which does not execute <script> tags,
   * so this is re-read on every bind rather than captured once at load.
   */
  var DEFAULT_ROUTES = {
    cart: '/cart',
    cartAdd: '/cart/add',
    cartChange: '/cart/change',
    cartUpdate: '/cart/update'
  };

  var cfg = { routes: DEFAULT_ROUTES, tiers: [], strings: {} };

  function readConfig() {
    var tag = document.getElementById('mmacd-drawer-config');
    if (!tag) return cfg;
    try {
      var parsed = JSON.parse(tag.textContent);
      parsed.routes = Object.assign({}, DEFAULT_ROUTES, parsed.routes || {});
      parsed.strings = parsed.strings || {};
      parsed.tiers = (parsed.tiers || []).filter(function (t) { return t && t.variantId; });
      parsed.priceByVariant = {};
      (parsed.insurancePrices || []).forEach(function (p) {
        if (p && p.id) parsed.priceByVariant[String(p.id)] = p.price;
      });
      cfg = parsed;
    } catch (e) {
      console.warn('[MMACartDrawer] config parse failed', e);
    }
    return cfg;
  }

  var state = { enabled: false, syncing: false, open: false, timer: null };

  function el(sel, ctx) { return (ctx || document).querySelector(sel); }
  function els(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function drawer() { return document.getElementById('sidebar-cart'); }

  function json(url, opts) {
    return fetch(url, Object.assign({
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin'
    }, opts || {})).then(function (r) {
      if (!r.ok) throw new Error(url + ' -> ' + r.status);
      return r.json();
    });
  }

  function getCart() { return json(cfg.routes.cart + '.js'); }

  /* Re-render the section server-side so Liquid stays the source of truth for
   * money formatting, locale strings and line item markup.
   */
  function refresh() {
    var d = drawer();
    if (!d) return Promise.resolve();
    var url = '?sections=' + encodeURIComponent(cfg.sectionId);
    var scroll = el('[data-scrollable]', d);
    var scrollTop = scroll ? scroll.scrollTop : 0;

    /* If the customer folded the upsells away, a cart change should not spring
     * them open again. null means the block is not collapsible at all. */
    var upsellsEl = el('[data-upsells]', d);
    var upsellsOpen = upsellsEl && upsellsEl.classList.contains('is-collapsible')
      ? upsellsEl.classList.contains('is-open')
      : null;

    /* Section HTML and cart are fetched together so the insurance price can be
     * corrected on the detached markup before it ever hits the DOM. */
    return Promise.all([
      fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.json(); }),
      getCart().catch(function () { return null; })
    ])
      .then(function (results) {
        var data = results[0];
        var cart = results[1];
        var html = data[cfg.sectionId];
        if (!html) return;
        var wrapper = document.getElementById('shopify-section-' + cfg.sectionId);
        if (!wrapper) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var next = parsed.getElementById('shopify-section-' + cfg.sectionId);
        if (!next) return;
        applyInsurancePrice(next, cart);
        applyShippingBar(next, cart);
        dropUpsellsAlreadyInCart(next, cart);
        var wasOpen = state.open;
        wrapper.innerHTML = next.innerHTML;
        bindDrawer();
        if (wasOpen) markOpen(true);
        if (upsellsOpen !== null) {
          var nextUpsells = el('[data-upsells]', drawer());
          if (nextUpsells) nextUpsells.classList.toggle('is-open', upsellsOpen);
        }
        var s = el('[data-scrollable]', drawer());
        if (s) s.scrollTop = scrollTop;
      })
      .catch(function (e) { console.warn('[MMACartDrawer] refresh failed', e); });
  }

  /* The section's Liquid cannot pick the tier itself: it has no access to
   * Shopify.currency.rate, so on a non-base-currency storefront it compares an
   * RSD total against dollar thresholds and always lands on the top tier.
   * The controller therefore owns the displayed price.
   */
  function insurancePriceLabel(cart) {
    if (!cfg.insuranceEnabled || !cart) return null;
    var tier = tierFor(goodsBaseCents(cart, cfg.insuranceProductId, baseRate()), cfg.tiers);
    if (!tier) return null;
    return (cfg.priceByVariant && cfg.priceByVariant[String(tier.variantId)]) || null;
  }

  /* Applied to a detached document fragment during refresh, so the corrected
   * price is already in the markup at the moment it is inserted. Writing it
   * after insertion is what made the stale Liquid value flash. */
  function applyInsurancePrice(root, cart) {
    var label = insurancePriceLabel(cart);
    if (!label || !root) return;
    Array.prototype.forEach.call(root.querySelectorAll('[data-ins-price]'), function (n) {
      if (n.textContent !== label) n.textContent = label;
    });
  }

  function updateInsuranceDisplay(cart) { applyInsurancePrice(drawer(), cart); }

  /* Second line of defence on top of the Liquid filter. The section is rendered
   * server-side, so anything the cart gained since that render could still be
   * offered back to the customer. Cards are removed rather than hidden so the
   * rail has no gaps, and the whole block goes if nothing is left. */
  function dropUpsellsAlreadyInCart(root, cart) {
    if (!root || !cart) return;
    var inCart = {};
    (cart.items || []).forEach(function (i) { inCart[String(i.product_id)] = true; });

    Array.prototype.forEach.call(root.querySelectorAll('[data-upsell-card]'), function (card) {
      var pid = card.getAttribute('data-product-id');
      if (pid && inCart[pid]) card.parentNode.removeChild(card);
    });

    ['[data-upsells]', '[data-recs]'].forEach(function (sel) {
      var block = root.querySelector(sel);
      if (!block) return;
      if (!block.querySelector('[data-upsell-card]')) {
        var target = sel === '[data-recs]' ? block : block;
        if (target.parentNode) target.parentNode.removeChild(target);
        if (sel === '[data-recs]') {
          var head = root.querySelector('.mmacd-drawer__recshead');
          if (head && head.parentNode) head.parentNode.removeChild(head);
        }
      }
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Only reached when the storefront is not in the shop's base currency. In
   * base currency the shop money format is exact, so it is used verbatim. */
  function formatPresentment(cents, cart) {
    if (baseRate() === 1) return moneyFormat(cents, cfg.moneyFormat);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: (cart && cart.currency) || 'USD'
      }).format(cents / 100);
    } catch (e) {
      return moneyFormat(cents, cfg.moneyFormat);
    }
  }

  /* Same defect as the insurance price: the section's Liquid compares a
   * presentment-currency cart total against a base-currency threshold, so on a
   * non-base storefront it always reads as qualified and pins the bar at 100%.
   * Applied to detached markup during refresh so it never flashes.
   */
  function applyShippingBar(root, cart) {
    if (!root || !cart || !cfg.showThreshold || !cfg.threshold) return;
    var wrap = root.querySelector('[data-shipping-bar]');
    if (!wrap) return;
    var p = shippingProgress(cart, cfg.threshold, baseRate());
    var fill = wrap.querySelector('.mmacd-drawer__fill');
    if (fill) fill.style.width = p.percent + '%';
    var msg = wrap.querySelector('.mmacd-drawer__shipmsg');
    if (!msg) return;
    /* Never blank the message. If a string is missing from the config, leave
     * whatever Liquid rendered rather than wiping the line. */
    if (p.qualified) {
      if (cfg.strings.unlocked) msg.textContent = cfg.strings.unlocked;
    } else if (cfg.strings.awayTemplate) {
      var amount = escapeHtml(formatPresentment(p.remaining, cart));
      msg.innerHTML = String(cfg.strings.awayTemplate)
        .replace('[amount]', '<span>' + amount + '</span>');
    }
  }

  /* Insurance writes are serialised through a single-slot intent queue.
   *
   * The previous version returned early while a write was in flight, which
   * silently DROPPED the click. The re-render then restored the server state
   * and the checkbox appeared to snap back on its own. A click must never be
   * discarded: if one is in flight, the newest intent is parked and applied
   * straight after, and only the newest one survives so a burst of clicks
   * collapses to a single final write.
   */
  var ins = { busy: false, pending: null };

  function applyInsurance(target) {
    ins.busy = true;
    return getCart()
      .then(function (cart) {
        updateInsuranceDisplay(cart);
        var updates = insuranceUpdates(cart, cfg, target);
        if (!updates) return false;
        return json(cfg.routes.cartUpdate + '.js', {
          method: 'POST',
          body: JSON.stringify({ updates: updates })
        }).then(function () { return true; });
      })
      .then(function (changed) { return changed ? refresh() : null; })
      .catch(function (e) {
        console.warn('[MMACartDrawer] insurance update failed', e);
        return refresh();
      })
      .then(function () {
        ins.busy = false;
        if (ins.pending !== null) {
          var next = ins.pending;
          ins.pending = null;
          return applyInsurance(next);
        }
        return null;
      });
  }

  function setInsurance(on) {
    if (ins.busy) { ins.pending = !!on; return Promise.resolve(); }
    return applyInsurance(!!on);
  }

  /* Re-tiering pass. Never runs while a user-initiated write is in flight, so
   * it cannot race the checkbox. */
  function syncInsurance() {
    if (ins.busy) return Promise.resolve();
    return getCart()
      .then(function (cart) {
        /* Display refresh runs regardless of whether insurance is enabled,
         * otherwise the shipping bar would go stale on stores with the
         * insurance upsell switched off. */
        updateInsuranceDisplay(cart);
        applyShippingBar(drawer(), cart);
        dropUpsellsAlreadyInCart(drawer(), cart);
        if (!cfg.insuranceEnabled) return null;
        if (!isInsuranceChecked(cart, cfg.insuranceProductId)) return null;
        var updates = insuranceUpdates(cart, cfg, true);
        if (!updates) return null;
        ins.busy = true;
        return json(cfg.routes.cartUpdate + '.js', {
          method: 'POST',
          body: JSON.stringify({ updates: updates })
        })
          .then(function () { return refresh(); })
          .then(function () { ins.busy = false; });
      })
      .catch(function (e) {
        ins.busy = false;
        console.warn('[MMACartDrawer] insurance sync failed', e);
      });
  }

  function scheduleSync() {
    clearTimeout(state.timer);
    state.timer = setTimeout(syncInsurance, 300);
  }

  function addItem(variantId, quantity) {
    return json(cfg.routes.cartAdd + '.js', {
      method: 'POST',
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
    }).then(function () {
      return refresh();
    }).then(function () {
      scheduleSync();
      open();
    });
  }

  function shareCart() {
    return getCart().then(function (cart) {
      var url = cartPermalink(cart, location.origin, cfg.insuranceProductId);
      if (!url) return;
      if (navigator.share) {
        return navigator.share({ title: document.title, url: url }).catch(function () {});
      }
      return navigator.clipboard.writeText(url).then(function () { toast(cfg.strings.linkCopied); });
    });
  }

  function toast(msg) {
    var d = drawer();
    if (!d) return;
    var t = el('[data-drawer-toast]', d);
    if (!t) return;
    t.textContent = msg;
    t.hidden = false;
    clearTimeout(t._timer);
    t._timer = setTimeout(function () { t.hidden = true; }, 2200);
  }

  /* Variant sheet. Rows are cloned out of the card's hidden source list so a
   * twenty-variant product never inflates the upsell row height.
   */
  function openSheet(card) {
    var d = drawer();
    if (!d || !card) return;
    var sheet = el('[data-variant-sheet]', d);
    var list = el('[data-sheet-list]', d);
    var label = el('[data-sheet-product]', d);
    var source = el('[data-upsell-variants]', card);
    if (!sheet || !list || !source) return;

    list.innerHTML = '';
    Array.prototype.forEach.call(source.children, function (row) {
      list.appendChild(row.cloneNode(true));
    });
    if (label) label.textContent = card.getAttribute('data-product-title') || '';

    sheet.hidden = false;
    list.scrollTop = 0;
    var first = list.querySelector('[data-upsell-add]');
    if (first) first.focus();
  }

  function closeSheet() {
    var d = drawer();
    if (!d) return;
    var sheet = el('[data-variant-sheet]', d);
    if (sheet) sheet.hidden = true;
  }

  function markOpen(on) {
    var d = drawer();
    if (!d) return;
    state.open = !!on;
    d.setAttribute('aria-hidden', on ? 'false' : 'true');
    d.classList.toggle('is-open', !!on);
    document.documentElement.classList.toggle('mmacd-drawer-open', !!on);
  }

  function open() {
    if (!state.enabled) return;
    markOpen(true);
    publishCartViewed();
    var close = el('[data-action="close-drawer"]', drawer());
    if (close) close.focus();
  }

  function close() { closeSheet(); markOpen(false); }

  /* Restores the funnel step that control users get from the /cart pageview.
   * Native Meta/Google apps ignore custom events, but GA4-via-GTM and Visually
   * both consume it, which is what the test is actually read from.
   */
  function publishCartViewed() {
    if (!cfg.emitCartViewed) return;
    try {
      root.dataLayer = root.dataLayer || [];
      root.dataLayer.push({ event: 'view_cart', source: 'mma_cart_drawer' });
    } catch (e) { /* no-op */ }
  }

  function bindDrawer() {
    readConfig();
    var d = drawer();
    if (!d || !state.enabled) return;

    els('[data-action="close-drawer"]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });

    var share = el('[data-action="share-cart"]', d);
    if (share) share.addEventListener('click', function (e) { e.preventDefault(); shareCart(); });

    var box = el('[data-insurance-toggle]', d);
    if (box) box.addEventListener('change', function () { setInsurance(box.checked); });

    els('[data-insurance-set]', d).forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        setInsurance(b.getAttribute('data-insurance-set') === '1');
      });
    });

    var upsellToggle = el('[data-upsell-toggle]', d);
    if (upsellToggle) {
      upsellToggle.addEventListener('click', function (e) {
        e.preventDefault();
        var wrap = el('[data-upsells]', d);
        if (wrap) wrap.classList.toggle('is-open');
      });
    }

    /* Delegated, because sheet rows are cloned in after this runs. */
    d.addEventListener('click', function (e) {
      var addBtn = e.target.closest && e.target.closest('[data-upsell-add]');
      if (addBtn) {
        e.preventDefault();
        if (addBtn.disabled) return;
        addBtn.disabled = true;
        closeSheet();
        addItem(addBtn.getAttribute('data-upsell-add'), 1);
        return;
      }

      var optBtn = e.target.closest && e.target.closest('[data-upsell-options]');
      if (optBtn) {
        e.preventDefault();
        openSheet(optBtn.closest('[data-upsell-card]'));
        return;
      }

      if (e.target.closest && e.target.closest('[data-sheet-close]')) {
        e.preventDefault();
        closeSheet();
        return;
      }

      /* Backdrop tap: the panel is a child, so only a hit on the sheet itself
       * counts as outside. */
      if (e.target.hasAttribute && e.target.hasAttribute('data-variant-sheet')) {
        closeSheet();
      }
    });

    els('[data-cart-change]', d).forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        var key = b.getAttribute('data-line-key');
        var qty = parseInt(b.getAttribute('data-cart-change'), 10);
        json(cfg.routes.cartChange + '.js', {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: qty })
        }).then(refresh).then(scheduleSync);
      });
    });

    /* Deliberately no scheduleSync() here. refresh() already corrects the price
     * before insertion, and a timer firing after every re-render opened a
     * window where a checkbox click landed mid-sync and was dropped. */
  }

  /* ATC interception.
   *
   * Capture phase + stopPropagation is load bearing: if Prestige's own handler
   * also runs we get two /cart/add.js calls, which means two Shopify
   * product_added_to_cart events and therefore a duplicated Meta AddToCart on
   * the variant arm only. That would bias the test.
   */
  function onSubmit(e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    var data = new FormData(form);
    fetch(cfg.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      body: data
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refresh(); })
      .then(function () { scheduleSync(); open(); })
      .catch(function (err) {
        console.warn('[MMACartDrawer] add failed, falling back to native submit', err);
        form.submit();
      });
  }

  function onClick(e) {
    var link = e.target.closest && e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
    if (!link) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    refresh().then(open);
  }

  /* Escape closes the sheet first, then the drawer. Closing both at once loses
   * the customer's place in a long variant list. */
  function onKey(e) {
    if (e.key !== 'Escape') return;
    var d = drawer();
    var sheet = d && el('[data-variant-sheet]', d);
    if (sheet && !sheet.hidden) { closeSheet(); return; }
    if (state.open) close();
  }

  function enable() {
    if (state.enabled) return;
    state.enabled = true;
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey);
    document.documentElement.setAttribute('data-mmacd-drawer', 'on');
    bindDrawer();
    scheduleSync();
  }

  function disable() {
    if (!state.enabled) return;
    state.enabled = false;
    document.removeEventListener('submit', onSubmit, true);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKey);
    document.documentElement.removeAttribute('data-mmacd-drawer');
    close();
  }

  root.MMACartDrawer = Object.assign({
    enable: enable,
    disable: disable,
    open: open,
    close: close,
    refresh: refresh,
    addItem: addItem,
    setInsurance: setInsurance,
    shareCart: shareCart,
    syncInsurance: syncInsurance,
    isEnabled: function () { return state.enabled; }
  }, logic);

  if (document.readyState !== 'loading') bindDrawer();
  else document.addEventListener('DOMContentLoaded', bindDrawer);
})(typeof window !== 'undefined' ? window : globalThis);
